import { useContext, useMemo } from 'react';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { UserPreferencesContext } from '../userPreferenceContext';
import { deriveEffectiveTaxonomiesForScope } from '../utils/derive-effective-taxonomies';

export function useRoleTaxonomySelection():
  | CustomRoleTaxonomySelection
  | undefined {
  const { preferences, scope } = useContext(UserPreferencesContext);

  const selection = useMemo<CustomRoleTaxonomySelection | undefined>(() => {
    // The merged view stores the raw GLOBAL choice; derive it for this page's
    // scope first so a non-company 'adaptive' resolves to Generic instead of
    // reading a company-page adaptive resolution (or staying undefined).
    const taxonomies = deriveEffectiveTaxonomiesForScope(
      preferences.taxonomies,
      scope
    );
    const rolePref = taxonomies?.role as
      | CustomRoleTaxonomySelection
      | 'adaptive'
      | undefined;

    if (!rolePref || rolePref === 'adaptive') {
      // When adaptive, use the effective selection if available (computed
      // by adaptive taxonomy hooks). Return undefined while unresolved so
      // callers can pause queries instead of fetching with {default:true}.
      const effective = taxonomies?.effectiveRoleTaxonomySelection as
        | CustomRoleTaxonomySelection
        | undefined;
      return effective && typeof effective === 'object'
        ? { ...effective }
        : undefined;
    }
    if (typeof rolePref === 'object') return { ...rolePref };
    return { default: true };
  }, [preferences, scope]);

  return selection;
}
